import { useState } from 'react';

import type {
  BroadcastContent,
  ConnectingLineDelayProperties,
  DelayBroadcastPattern,
} from '../../../api/types';
import { CONNECTING_GRANULARITY_DEFAULT_SSML } from '../../../utils/broadcastContents';
import { createDefaultPattern } from '../logic';
import { AudioPreviewField } from '../parts/AudioPreviewField';
import { PlaceholderLegend } from '../parts/PlaceholderLegend';
import { CONNECTING_TRANSFER_AWARE_PLACEHOLDERS } from '../placeholders';
import { deriveContentSampleOverrides, deriveSampleDelaySeconds } from '../previewSample';
import type { PreviewSsmlHandler } from '../types';
import { DelayPatternRow } from './DelayPatternRow';

/**
 * 接続線遅延 (transfer_aware モード) 用の表形式エディタ。
 *
 * 自列車遅延の `SelfTrainDelayFields` と同じ構造で、遅れ分数の幅ごとに 1 行ずつ
 * 「遅れ分数 / 放送粒度 / 放送パターン (SSMLテンプレート) / 行操作 (＋−)」を表示する。
 * 行コンポーネント (`DelayPatternRow`) は自列車遅延と共通化しているため、testid プレフィックスを
 * `connecting-line-delay-ssml-template` に差し替えて自列車遅延の <textarea> と区別する。
 */
export function ConnectingLineDelayPatternsTable({
  props,
  threshold,
  content,
  contentIndex = 0,
  parentVoiceId,
  playbackRate,
  onPreviewSsml,
  onChange,
}: {
  props: Partial<ConnectingLineDelayProperties>;
  threshold: number;
  /** プレビュー対象の放送コンテンツ (Issue #439) */
  content?: BroadcastContent;
  /** `data-testid` を一意にするためのコンテンツ index (Issue #439) */
  contentIndex?: number;
  /** 親フォームで選択中の Polly VoiceId (実放送と同じ声色で試聴するため) */
  parentVoiceId?: string;
  /** 親コンテンツの `playback_rate` (プレビュー音声にも反映する) */
  playbackRate?: number;
  /** 音声プレビュー処理。省略時はプレビュー UI を表示しない */
  onPreviewSsml?: PreviewSsmlHandler;
  onChange: (props: ConnectingLineDelayProperties) => void;
}): React.JSX.Element {
  /*
   * プレビューで採用された遅延パターン行の index (Issue #439)。
   * サーバの `select_delay_pattern()` の結果をそのまま反映してハイライトする。
   */
  const [matchedPatternIndex, setMatchedPatternIndex] = useState<number | null>(null);
  const existingPatterns: DelayBroadcastPattern[] = Array.isArray(props.delay_patterns)
    ? props.delay_patterns
    : [];
  const patterns: DelayBroadcastPattern[] =
    existingPatterns.length > 0
      ? existingPatterns
      : [createDefaultPattern(CONNECTING_GRANULARITY_DEFAULT_SSML)];

  /** delay_patterns を更新し、必須プロパティ (route_id / threshold) を維持して onChange を呼ぶ */
  const emit = (nextPatterns: DelayBroadcastPattern[]): void => {
    onChange({
      ...props,
      route_id: props.route_id ?? '',
      delay_threshold_seconds: threshold,
      delay_patterns: nextPatterns,
    } as ConnectingLineDelayProperties);
  };

  const updatePattern = (index: number, patch: Partial<DelayBroadcastPattern>): void => {
    const next = patterns.slice();
    const current = next[index]!;
    // 粒度が変わったら接続線 transfer_aware 用のデフォルト SSML 文に上書きする。
    // (ユーザ要望: 「粒度を変えたら元々のデフォルト値を再設定して」)
    if (patch.granularity && patch.granularity !== current.granularity) {
      next[index] = {
        ...current,
        ...patch,
        ssml_template: CONNECTING_GRANULARITY_DEFAULT_SSML[patch.granularity],
      };
    } else {
      next[index] = { ...current, ...patch };
    }
    emit(next);
  };

  const addPatternAfter = (index: number): void => {
    const next = patterns.slice();
    next.splice(index + 1, 0, createDefaultPattern(CONNECTING_GRANULARITY_DEFAULT_SSML));
    emit(next);
  };

  const removePattern = (index: number): void => {
    if (patterns.length <= 1) return;
    const next = patterns.slice();
    next.splice(index, 1);
    emit(next);
  };

  return (
    <div className="connecting-line-delay-patterns" data-testid="connecting-line-delay-patterns">
      <table className="delay-patterns-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              遅れ分数
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px', whiteSpace: 'nowrap' }}>
              放送粒度
            </th>
            <th style={{ textAlign: 'left', padding: '4px 6px' }}>
              放送パターン (SSMLテンプレート)
            </th>
            <th
              aria-label="行操作"
              style={{ textAlign: 'center', padding: '4px 6px', whiteSpace: 'nowrap' }}
            />
          </tr>
        </thead>
        <tbody>
          {patterns.map((pattern, i) => (
            <DelayPatternRow
              key={i}
              index={i}
              pattern={pattern}
              canRemove={patterns.length > 1}
              ssmlTestIdPrefix="connecting-line-delay-ssml-template"
              placeholderTemplates={CONNECTING_GRANULARITY_DEFAULT_SSML}
              // Issue #439: 行ごとに「▶ この文面を試聴」を出す。
              // サンプル条件の既定値はコンテンツ設定 (乗換駅 / 線名 / 方面) から導出し、
              // 遅延分数だけは **この行が選ばれる値** で上書きする
              // (行ごとに試聴する UI なので、その行の境界値で鳴らないと意味がない)。
              // ユーザーが条件パネルで指定した値は上書きされない (`usePreviewSample`)。
              previewSlot={
                content ? (
                  <AudioPreviewField
                    content={content}
                    parentVoiceId={parentVoiceId}
                    playbackRate={playbackRate}
                    onPreviewSsml={onPreviewSsml}
                    testIdSuffix={`connecting-line-delay-${contentIndex}-${i}`}
                    label="▶ この文面を試聴"
                    placeholders={CONNECTING_TRANSFER_AWARE_PLACEHOLDERS}
                    sampleOverrides={{
                      ...deriveContentSampleOverrides(content),
                      delaySeconds: deriveSampleDelaySeconds(pattern),
                    }}
                    disabled={(pattern.ssml_template ?? '').trim() === ''}
                    disabledReason="SSML を入力すると試聴できます"
                    onMatchedPatternIndexChange={setMatchedPatternIndex}
                  />
                ) : null
              }
              highlighted={matchedPatternIndex === i}
              onUpdate={(patch) => updatePattern(i, patch)}
              onAdd={() => addPatternAfter(i)}
              onRemove={() => removePattern(i)}
            />
          ))}
        </tbody>
      </table>

      <p className="hint" style={{ margin: '8px 0 4px' }}>
        各行の「遅れ分数」の範囲に隙間が生じないよう注意して設定してください。
        各行の「＋」で行を追加、「−」で行を削除できます。 SSML
        欄は必須項目です。各放送粒度のデフォルト文言があらかじめ入力されているので、必要に応じて書き換えてください。
      </p>
      <PlaceholderLegend
        testId="connecting-line-delay-pattern-hint"
        items={CONNECTING_TRANSFER_AWARE_PLACEHOLDERS}
      />
    </div>
  );
}
